/**
 * What changed between two versions, word by word.
 *
 * WHY WORDS AND NOT CHARACTERS. A character diff of a procedure marks the inside of words — "121"
 * becoming "134" shows as keep-1, drop-2, add-3, keep-nothing — which is accurate and unreadable.
 * A reader wants to see "121 °C" struck and "134 °C" added. Words are the unit people read in.
 *
 * WHY TEXT AND NOT THE DOCUMENT TREE. A structural diff would also report that a paragraph became
 * a heading, which matters less in a procedure than what it now says, and costs an order of
 * magnitude more to get right. This answers "what words changed", which is the question somebody
 * opening a version actually has. It is NOT a merge tool and must never be mistaken for one.
 *
 * AND IT IS A READING AID, NOT A RECORD. The audit trail is the record; this is drawn on demand
 * from two bodies and stored nowhere.
 */

export type DiffPart = {
    kind: "same" | "added" | "removed";
    text: string;
};

type Token = {
    /** The word with its surrounding whitespace, which is what gets displayed. */
    raw: string;
    /** The word alone, which is what two versions are compared on. */
    key: string;
};

/**
 * Split into words, keeping the whitespace for display and comparing on the word alone.
 *
 * THE TWO ARE SEPARATE ON PURPOSE. Comparing the raw token means a word whose trailing space
 * changed reads as edited -- append a sentence to a paragraph and the word before it is marked
 * changed as well, which is noise sitting right where the real change is. The reader sees the
 * spacing; the comparison does not.
 */
function tokens(text: string): Token[] {
    return (text.match(/\s*\S+\s*/g) ?? []).map(raw => ({ raw, key: raw.trim() }));
}

/**
 * The longest common subsequence of two word lists, as a table of lengths.
 *
 * BOUNDED, because this runs in the browser on a document somebody is waiting to read. An LCS is
 * quadratic, so two 20,000-word procedures would be 400 million cells -- several seconds and a
 * great deal of memory, for a view nobody asked to wait for. Past the cap the caller is told the
 * comparison is too large rather than being made to wait.
 */
const MAX_WORDS = 4000;

export const TOO_LARGE = "too-large";

export function diffWords(before: string, after: string): DiffPart[] | typeof TOO_LARGE {
    const a = tokens(before);
    const b = tokens(after);
    if (a.length > MAX_WORDS || b.length > MAX_WORDS) return TOO_LARGE;

    // table[i][j] = length of the LCS of a[i:] and b[j:]
    const table: number[][] = Array.from({ length: a.length + 1 },
                                         () => new Array(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i -= 1) {
        for (let j = b.length - 1; j >= 0; j -= 1) {
            table[i][j] = a[i].key === b[j].key
                ? table[i + 1][j + 1] + 1
                : Math.max(table[i + 1][j], table[i][j + 1]);
        }
    }

    const parts: DiffPart[] = [];
    const push = (kind: DiffPart["kind"], text: string) => {
        const last = parts[parts.length - 1];
        // RUNS ARE MERGED as they are produced, so a sentence of new text is one part rather than
        // eleven: the renderer underlines a run once, and a reader sees a phrase rather than a
        // stutter of separately marked words.
        if (last && last.kind === kind) last.text += text;
        else parts.push({ kind, text });
    };

    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
        if (a[i].key === b[j].key) {
            // THE LATER SPELLING OF THE WHITESPACE, because the "after" text is the one being
            // shown; the words are identical either way.
            push("same", b[j].raw);
            i += 1;
            j += 1;
        } else if (table[i + 1][j] >= table[i][j + 1]) { push("removed", a[i].raw); i += 1; }
        else { push("added", b[j].raw); j += 1; }
    }
    while (i < a.length) { push("removed", a[i].raw); i += 1; }
    while (j < b.length) { push("added", b[j].raw); j += 1; }

    return parts;
}

/** Everything a reader would read, in document order — the same shape the search index uses. */
export function plainText(body: any): string {
    if (!body || typeof body !== "object") return "";
    if (body.type === "text") return body.text ?? "";
    const inner = (body.content ?? []).map(plainText).join("");
    // BLOCKS ARE SEPARATED, or the last word of a paragraph and the first of the next become one
    // word and every diff after a paragraph break is wrong.
    return ["paragraph", "heading", "listItem", "blockquote", "tableRow"].includes(body.type)
        ? `${inner}\n` : inner;
}
